/**
 * The one way to choose files (wave W1.5, Mahmoud's walk finding 3).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * He added one document, then a second, and the second REPLACED the first: the picker
 * kept only the latest selection. Here every pick APPENDS (several files at once or one
 * at a time), the list shows each file with its size, and each can be removed on its own
 * before anything is sent. Picking the same file twice keeps one copy. The input is
 * cleared after each pick, so the same file can be chosen again after a removal.
 *
 * The upload gate (`src/lib/uploads.test.ts`) fails on any `type="file"` input in the
 * product that is not this component.
 */
import { Paperclip, X } from "lucide-react";
import { useId, useState } from "react";

import { strings } from "../../brand/strings";
import { Button } from "./button";

/** Two picks of the same file are one file. */
function sameFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

export function appendFiles(current: readonly File[], picked: readonly File[]): File[] {
  const next = [...current];
  for (const file of picked) {
    // An empty file is refused by the server's upload (attachment.non_empty_required):
    // it is never added, and the picker says so beside the list.
    if (file.size > 0 && !next.some((one) => sameFile(one, file))) {
      next.push(file);
    }
  }
  return next;
}

function size(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${String(Math.max(1, Math.round(bytes / 1024)))} kB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FilePicker({
  files,
  onChange,
  label,
  testId,
  disabled = false,
  emptyText,
}: {
  readonly files: readonly File[];
  readonly onChange: (next: File[]) => void;
  readonly label: string;
  /** The input's test id; the list is `${testId}-list`, each row `${testId}-item`. */
  readonly testId: string;
  readonly disabled?: boolean;
  readonly emptyText?: string;
}): React.JSX.Element {
  const id = useId();
  const [skipped, setSkipped] = useState<readonly string[]>([]);
  return (
    <div className="q-ui flex flex-col gap-2" data-slot="file-picker">
      <input
        id={id}
        type="file"
        multiple
        disabled={disabled}
        className="peer sr-only"
        data-testid={testId}
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          setSkipped(picked.filter((one) => one.size === 0).map((one) => one.name));
          onChange(appendFiles(files, picked));
          // Cleared, so the same file can be picked again after it was removed.
          event.target.value = "";
        }}
      />
      {/* The input is visually hidden; its keyboard focus is drawn on the label that
          stands for it, so a keyboard user sees where they are. */}
      <Button
        asChild
        variant="outline"
        className="self-start peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring peer-focus-visible:outline-solid"
        aria-disabled={disabled}
      >
        <label htmlFor={id}>
          <Paperclip aria-hidden="true" />
          {label}
        </label>
      </Button>
      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`${testId}-list`}>
          {emptyText ?? strings.files.none}
        </p>
      ) : (
        <ul className="flex flex-col gap-1" data-testid={`${testId}-list`}>
          {files.map((file, index) => (
            <li
              key={`${file.name} ${String(file.size)} ${String(file.lastModified)}`}
              className="flex min-h-11 items-center justify-between gap-3 rounded-md border border-rule px-3"
              data-testid={`${testId}-item`}
            >
              <span className="min-w-0 truncate text-sm text-foreground">
                {file.name} <span className="text-muted-foreground">({size(file.size)})</span>
              </span>
              <Button
                variant="ghost"
                size="icon"
                disabled={disabled}
                aria-label={strings.files.remove(file.name)}
                data-testid={`${testId}-remove`}
                onClick={() => {
                  onChange(files.filter((_, at) => at !== index));
                }}
              >
                <X aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {skipped.length > 0 && (
        <p className="text-sm text-danger" role="alert" data-testid={`${testId}-skipped`}>
          {strings.files.emptySkipped(skipped)}
        </p>
      )}
      {files.length > 1 && (
        <p className="text-sm text-muted-foreground" data-testid={`${testId}-count`}>
          {strings.files.count(files.length)}
        </p>
      )}
    </div>
  );
}
